import type { SocialMediaReport, TopPost } from "../hooks/useSocialMediaReport";
import { dayIndex, type DayRange } from "./dayAxis";
import { parseGraphTime, romeDate } from "./dayMarkers";

// As many as a month report ranks.
const TOP_POSTS = 10;

/** A top post's Rome day: that of its earliest platform post. */
const publishedOn = (post: TopPost): string | null => {
  const times = Object.values(post.platforms).flatMap((entry) => {
    const time = entry?.post.createdTime ?? entry?.post.timestamp;
    return time && !Number.isNaN(parseGraphTime(time)) ? [time] : [];
  });
  if (times.length === 0) return null;
  const earliest = times.reduce((a, b) => (parseGraphTime(b) < parseGraphTime(a) ? b : a));
  return romeDate(earliest);
};

/** One post's key across reports: its internal post, else its first
 *  platform post. */
const keyOf = (post: TopPost): string => {
  if (post.postId) return post.postId;
  const [platform, entry] = Object.entries(post.platforms).find(([, e]) => e) ?? [];
  return entry ? `${platform}:${entry.post.id}` : "";
};

/** The posts published inside `range` (the Rome day of their earliest
 *  platform post), from the month reports' topPosts: a post in two months'
 *  lists counts once, its engagementScore summed across them (each month's
 *  score is that month's increment), shown with the latest month's entry;
 *  ranked by the sum, at most `limit`. Reports come oldest first. */
export const windowTopPosts = (
  reports: ReadonlyArray<SocialMediaReport>,
  range: DayRange,
  limit = TOP_POSTS,
): TopPost[] => {
  const byKey = new Map<string, TopPost>();
  reports.forEach((report) =>
    report.topPosts.forEach((post) => {
      const day = publishedOn(post);
      const key = keyOf(post);
      if (!day || !key || dayIndex(range, day) == null) return;
      const seen = byKey.get(key);
      byKey.set(key, { ...post, engagementScore: (seen?.engagementScore ?? 0) + post.engagementScore });
    }),
  );
  return Array.from(byKey.values())
    .sort((a, b) => b.engagementScore - a.engagementScore)
    .slice(0, limit);
};
